import { platformApiClient } from '@/services/platform-api-client';

/**
 * Phase 12.7a — Platform Audit Logs API.
 *
 * Design of record: `docs/platform-designs/AUDIT_LOGS_DESIGN.md` v1.0 (APPROVED).
 *
 * ── One source, one endpoint (§7) ──────────────────────────────────────────
 * This binds to the SAME `GET /platform/audit-logs` the Dashboard's Recent
 * Activity already uses. There is no second event store, no duplicated row and
 * no divergent copy — the two surfaces differ only in volume, filters and detail.
 *
 * ── The response envelope (CLAUDE.md) ──────────────────────────────────────
 * `platformApiClient`'s response interceptor already unwraps `{ data, meta }`,
 * so `r.data` IS the payload here. A second `.data` access would yield
 * `undefined`.
 */

/** One audit entry. Mirrors `PlatformAuditLogEntryDto` exactly. */
export interface PlatformAuditLogEntry {
  id: string;
  actorId: string;
  actorEmail: string;
  actorRole: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  /** ISO-8601 string over the wire, even though the DTO types it as `Date`. */
  occurredAt: string;
}

export interface PlatformAuditLogPage {
  items: PlatformAuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * The query the page may issue.
 *
 * **D-1 — exactly two filters.** Date range and action. There is deliberately no
 * `actorEmail`, `resourceType`, `resourceId`, `tenant` or free-text `search`
 * field: some do not exist in the schema at all, and the backend rejects unknown
 * parameters rather than ignoring them, so adding one here would fail loudly.
 */
export interface PlatformAuditLogQuery {
  limit?: number;
  offset?: number;
  /** Inclusive lower bound on `occurredAt` — half-open `[from, to)`. */
  from?: string;
  /** Exclusive upper bound on `occurredAt`. */
  to?: string;
  /** A single action from the server-side whitelist. */
  action?: string;
}

export async function fetchPlatformAuditLogs(
  query: PlatformAuditLogQuery,
  signal?: AbortSignal,
): Promise<PlatformAuditLogPage> {
  const params: Record<string, string | number> = {};
  if (query.limit !== undefined) params['limit'] = query.limit;
  if (query.offset !== undefined) params['offset'] = query.offset;
  if (query.from !== undefined && query.from !== '') params['from'] = query.from;
  if (query.to !== undefined && query.to !== '') params['to'] = query.to;
  if (query.action !== undefined && query.action !== '') params['action'] = query.action;

  const r = await platformApiClient.get('/platform/audit-logs', {
    params,
    ...(signal ? { signal } : {}),
  });

  const body = r.data as Partial<PlatformAuditLogPage> | null | undefined;
  // Guard the list: a malformed body must render as "no rows", never crash the
  // page into a blank screen that reads as "no audit activity".
  return {
    items: Array.isArray(body?.items) ? (body.items as PlatformAuditLogEntry[]) : [],
    total: typeof body?.total === 'number' ? body.total : 0,
    limit: typeof body?.limit === 'number' ? body.limit : (query.limit ?? 20),
    offset: typeof body?.offset === 'number' ? body.offset : (query.offset ?? 0),
  };
}
